// v2 fishing hut — a stilted boathouse: a gable-roofed boathouse across the back
// of a raised plank deck on six pilings, a dark launch opening in its front wall
// feeding a slipway that runs off the deck edge down toward the water, an
// upturned skiff drying on the deck, a big net reel drum, a rack of hanging fish,
// cork buoys and a crab pot. Footprint-native 2x2. Ported 1:1 from age-of-mcp's
// v2/fishing_hut.ts, using the library's shared `foundationSlab`, `scaffold` and
// `industry` (gableRoof) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, spin, sway, EFFECT_LOOP, type Part } from "../../lib";

const DECK   = "#9a7448";   // plank deck
const DECKD  = "#7a5a36";   // slipway planks
const PILING = "#5e4528";   // pilings
const HUT    = "#7a5230";   // boathouse timber
const ROOF   = "#4f3320";
const RIDGE  = "#3a2515";
const DARKO  = "#241c14";   // launch opening
const NET    = "#caa86a";   // net reel
const FISH   = "#8aa6b0";   // hanging fish
const BOAT   = "#8a5e34";   // upturned skiff
const BUOY   = "#d86038";   // cork buoys
const WOODD  = "#5e4528";   // posts / crate

const DECK_TOP = 0.50;      // plank deck surface

// The raised plank deck on its six pilings (deck stops short of the front so
// the slipway can run off it).
function deck(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(1.70, 0.08, 1.50), color: DECK,
      position: [0, 0.46, -0.10] },
  ];
  for (const [px, pz] of [[-0.75, -0.80], [0.75, -0.80], [-0.75, -0.10], [0.75, -0.10], [-0.75, 0.60], [0.75, 0.60]] as const) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.07, 0.08, 0.46, 5), color: PILING,
      position: [px, 0.23, pz],
    });
  }
  return parts;
}

// The boathouse across the back of the deck, `h` of wall, with its dark
// launch opening; the gable roof comes with the finished stage.
function boathouse(h: number): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(1.26, h, 0.78), color: HUT,
      position: [-0.12, DECK_TOP + h / 2, -0.46] },
  ];
  if (h > 0.4) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.44, 0.36, 0.04), color: DARKO,
      position: [-0.30, 0.70, -0.06],
    });
  }
  return parts;
}

// The slipway: an inclined plank ramp from the deck edge down toward the
// water, with low side rails.
function slipway(): Part[] {
  const tilt = 0.727;
  return [
    { geometry: new THREE.BoxGeometry(0.40, 0.04, 0.38), color: DECKD,
      position: [-0.30, 0.38, 0.785], rotation: [tilt, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.04, 0.07, 0.38), color: PILING,
      position: [-0.48, 0.40, 0.785], rotation: [tilt, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.04, 0.07, 0.38), color: PILING,
      position: [-0.12, 0.40, 0.785], rotation: [tilt, 0, 0] },
  ];
}

// An upturned skiff drying on the deck beside the slipway.
function skiff(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.26, 0.08, 0.50), color: BOAT,
      position: [0.14, 0.545, 0.42], rotation: [0, 0.25, 0] },
    { geometry: new THREE.BoxGeometry(0.03, 0.03, 0.46), color: WOODD,
      position: [0.14, 0.595, 0.42], rotation: [0, 0.25, 0] },
  ];
}

// The net reel: a big drum slung between two posts on the east deck.
function netReel(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.04, 0.30, 0.04), color: WOODD,
      position: [0.55, 0.65, -0.15] },
    { geometry: new THREE.BoxGeometry(0.04, 0.30, 0.04), color: WOODD,
      position: [0.55, 0.65, 0.19] },
    { geometry: new THREE.CylinderGeometry(0.11, 0.11, 0.30, 7), color: NET,
      position: [0.55, 0.78, 0.02], rotation: [Math.PI / 2, 0, 0], name: "reel" },
  ];
}

// The drying rack: a beam between two posts with three fish hanging off it.
function fishRack(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.04, 0.42, 0.04), color: WOODD,
      position: [0.20, 0.71, 0.45] },
    { geometry: new THREE.BoxGeometry(0.04, 0.42, 0.04), color: WOODD,
      position: [0.82, 0.71, 0.45] },
    { geometry: new THREE.BoxGeometry(0.66, 0.04, 0.04), color: WOODD,
      position: [0.51, 0.94, 0.45] },
    { geometry: new THREE.BoxGeometry(0.045, 0.13, 0.025), color: FISH,
      position: [0.35, 0.86, 0.45], rotation: [0, 0, 0.10], name: "fish" },
    { geometry: new THREE.BoxGeometry(0.045, 0.13, 0.025), color: FISH,
      position: [0.51, 0.85, 0.45], rotation: [0, 0, -0.08], name: "fish" },
    { geometry: new THREE.BoxGeometry(0.045, 0.13, 0.025), color: FISH,
      position: [0.67, 0.86, 0.45], rotation: [0, 0, 0.05], name: "fish" },
  ];
}

// Cork buoys and a crab pot dressing the deck.
function clutter(): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.05, 6, 5), color: BUOY,
      position: [0.75, 0.55, -0.35] },
    { geometry: new THREE.SphereGeometry(0.045, 6, 5), color: BUOY,
      position: [0.83, 0.545, -0.45] },
    { geometry: new THREE.BoxGeometry(0.16, 0.14, 0.16), color: WOODD,
      position: [-0.78, 0.57, 0.10], rotation: [0, 0.35, 0] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.84)];
  if (s === 1) return [foundationSlab(1.84), ...deck(), ...scaffold(1.6, 0.6)];
  if (s === 2) return [foundationSlab(1.84), ...deck(), ...boathouse(0.26), ...scaffold(1.6, 0.95)];
  return [
    foundationSlab(1.84),
    ...deck(),
    ...boathouse(0.46),
    ...industry.gableRoof(-0.12, -0.46, 0.96, 1.38, 0.92, 0.26, ROOF, RIDGE),
    ...slipway(),
    ...skiff(),
    ...netReel(),
    ...fishRack(),
    ...clutter(),
  ];
}

export const fishingHutV2 = defineModel({
  id: "fishing-hut-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Fishing hut (v2)",
  tags: ["building","fishing","hut","low-poly","age-of-mcp","fable","water","animated"],
  summary: "Low-poly fishing hut.",
  description: "A timber fishing hut on a jetty over the water, catching fish.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // Deck life in the breeze: the hanging fish swing from the rack beam
      // while the net reel slowly winds.
      id: "active",
      label: "Deck at work",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        sway("fish", [0.51, 0.94, 0.45], { axis: "x", amount: 0.10 }),
        spin("reel", [0.55, 0.78, 0.02], { axis: "z", turns: 1 }),
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0.45, 0.5, 0.55], facing: 0 },
  ],
  interactions: [
    { id: "fish", label: "Working the deck", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
